import { StyledScheduleStickyTitle } from '../../styles/ScheduleViewStyles'
import { Box, Drawer, Table, TableBody, TableRow, Tooltip, useMediaQuery, useTheme } from '@mui/material'
import { StyledTitle } from '../../styles/MasonryStyles'
import ScheduleModifyManyDialog from '../../components/Popup/ScheduleModifyMany/ScheduleModifyManyDialog'
import { SquareButton } from '../../styles/ButtonStyles'
import HistoryIcon from '@mui/icons-material/History'
import ModificationHistoryComponent from '../../components/Schedule/ModificationHistoryComponent'
import dayjs from 'dayjs'
import HoursComponent from '../../components/Schedule/HoursComponent'
import ScheduleDeletePopup from '../../components/Popup/ScheduleDeletePopup'
import ScheduleTableSection from './ScheduleTableSection'
import { t } from 'i18next'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppSelector, useNotification } from '../../store/redux/hooks'
import { getSelectedDate } from '../../store/redux/reducers/monthPickerSlice'
import DateFormat from '../../store/types/enums/DateFormat'
import { getCurrentUser } from '../../store/redux/reducers/currentUserSlice'
import {
  EditScheduleRequest,
  PlannedHours,
  useEditScheduleMutation,
} from '../../store/redux/reducers/scheduleDateSlice'
import { HoursInterval, ScheduleTableVariant as Variant, User, WorkDay } from '../../store/types'
import MasonryTile from '../../components/Masonry/MasonryTile'
import ScheduleTableHeader from './ScheduleTable.tsx/ScheduleTableHeader'
import { calculateTotalHours, checkIfAnyTimePeriodOverlaps, getAdjacentDates } from '../../store/utils'
import { StyledTableCell } from '../../styles/TableStyles'
import { getHeaderCells } from '../../store/utils/getScheduleTableCells'
import ToggleHistoryButton from '../../components/Button/ToggleHistoryButton'
import MasonryTileCopyHours from './MasonryTileCopyHours'
import { isEqual } from 'lodash'
import { useEditMode } from '../../store/redux/hooks'
import { sortIntervals } from './utils'

const MasonryTileHours = ({
  hours,
  reverseHours,
  setHours,
  copyInto = () => {},
  isPlannedHours,
  worklog,
  variant,
  isReadOnly = false,
  selectedUser,
  setIsChangePresent,
  oldHours,
}: {
  hours: PlannedHours
  reverseHours: PlannedHours
  setHours: (
    newHours: (prevState: PlannedHours) => {
      [p: string]: { from: string; id: string; to: string }[]
    }
  ) => void
  copyInto?: (
    newHours: (prevState: PlannedHours) => {
      [p: string]: { from: string; id: string; to: string }[]
    }
  ) => void
  isPlannedHours: boolean
  worklog?: WorkDay[]
  variant: Variant
  isReadOnly?: boolean
  selectedUser: User | null
  setIsChangePresent?: React.Dispatch<React.SetStateAction<boolean>>
  oldHours: PlannedHours
}) => {
  const [selectedSections, setSelectedSections] = useState<string[]>([])
  const [isHistoryVisible, setIsHistoryVisible] = useState(false)
  const [openedSection, setOpenedSection] = useState<string>('')
  const [isEditable, setIsEditable] = useState(false)
  const [lastSelectedSection, setLastSelectedSection] = useState<string | null>(null)
  const [stickyHeaderElement, setStickyHeaderElement] = useState<HTMLDivElement | null>(null)
  const [stickyOffset, setStickyOffset] = useState(0)

  const maxIntervalsNumber = 10
  const currentUser = useAppSelector(state => getCurrentUser(state))
  const [historyType, setHistoryType] = useState<string>('')
  const selectedDate = useAppSelector(state => getSelectedDate(state))
  const totalHours = useMemo(() => calculateTotalHours(hours), [hours])
  const actualYearMonth = useMemo(() => dayjs(selectedDate).format(DateFormat.YearMonth), [selectedDate])
  const dayKeys = useMemo(() => Object.keys(hours), [hours])
  const worklogByDate = useMemo<Partial<Record<string, WorkDay>>>(() => {
    if (!worklog) {
      return {}
    }

    return worklog.reduce<Partial<Record<string, WorkDay>>>((acc, workDay) => {
      acc[workDay.date] = workDay
      return acc
    }, {})
  }, [worklog])

  let employeeId = currentUser?.id === undefined ? '' : currentUser?.id

  const { displayNotification } = useNotification()
  const [editSchedule] = useEditScheduleMutation()
  const { setIsEditMode } = useEditMode()
  const stickyHeaderRef = useCallback((node: HTMLDivElement | null) => {
    setStickyHeaderElement(node)
  }, [])

  useEffect(() => {
    setSelectedSections([])
    setOpenedSection('')
  }, [selectedUser, selectedDate])

  const createRequest = async (date: string, plannedHours: PlannedHours) => {
    let plannedHoursForDate = plannedHours[date] || []
    if (plannedHoursForDate.length === 1 && plannedHoursForDate[0].from === '-' && plannedHoursForDate[0].to === '-') {
      plannedHoursForDate = []
    }
    const requestBody: EditScheduleRequest = {
      startDate: date,
      endDate: date,
      hours: plannedHoursForDate.map(interval => ({
        from: interval.from,
        to: interval.to,
      })),
      frequency: 'EVERY_DAY',
      frequencyValue: {
        frequency: 1,
        selectedDays: [],
      },
    }

    plannedHoursForDate.forEach((interval: { from: string; to: string }) => {
      if (interval.from === '-' || interval.to === '-') {
        displayNotification({ message: t('page.schedule.noValidIntervals'), type: 'warning' })
        setHours(() => oldHours)
        return
      }
    })

    try {
      await editSchedule({
        employee: employeeId,
        status: isPlannedHours ? 'PLANNED' : 'TRACKED',
        requestBody,
        date: actualYearMonth,
      }).unwrap()
    } catch (error) {
      setHours(() => oldHours)
      displayNotification({ message: t('page.schedule.updateError'), type: 'error' })
    }
    setIsEditMode(false)
  }

  useEffect(() => {
    if (!stickyHeaderElement) {
      return
    }
    const updateOffset = () => {
      setStickyOffset(stickyHeaderElement.getBoundingClientRect().height)
    }

    updateOffset()
    const resizeObserver = new ResizeObserver(() => updateOffset())
    resizeObserver.observe(stickyHeaderElement)
    window.addEventListener('resize', updateOffset)
    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateOffset)
    }
  }, [stickyHeaderElement])

  const handleSectionExpansion = useCallback(
    (date: string) => {
      if (openedSection !== date) {
        setOpenedSection('')
        setTimeout(() => {
          setOpenedSection(date)
        }, 0)
      } else {
        setOpenedSection('')
      }
    },
    [setOpenedSection, openedSection]
  )

  useEffect(() => {
    const keyDownHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setOpenedSection('')
        setSelectedSections([])
        setLastSelectedSection(null)
        setIsEditable(false)
        setIsEditMode(false)
      }
    }

    document.addEventListener('keydown', keyDownHandler)

    return () => {
      document.removeEventListener('keydown', keyDownHandler)
    }
  }, [lastSelectedSection, handleSectionExpansion, setIsEditMode])

  const isAnySectionSelected = (): boolean => {
    return selectedSections.length !== 0
  }

  const editScheduleForAdjacentDates = async (adjacentDates: string[][]) => {
    try {
      await Promise.all(
        adjacentDates.map(async dates => {
          const requestBody: EditScheduleRequest = {
            startDate: dates[0],
            endDate: dates[dates.length - 1],
            hours: [],
            frequency: 'EVERY_DAY',
            frequencyValue: {
              frequency: 1,
              selectedDays: [],
            },
          }

          await editSchedule({
            employee: employeeId,
            status: isPlannedHours ? 'PLANNED' : 'TRACKED',
            requestBody,
            date: actualYearMonth,
          })
        })
      )
      displayNotification({ message: t('page.schedule.deleteSuccess'), type: 'success' })
    } catch (error) {
      displayNotification({ message: t('page.schedule.updateError'), type: 'error' })
    }
  }

  const deleteSelected = (selectedDates: string[]) => {
    const adjacentDates = getAdjacentDates(selectedDates, hours)
    editScheduleForAdjacentDates(adjacentDates)
  }

  const checkIfAllSelectedRowsAreEmpty = (selectedRows: string[], hours: PlannedHours): boolean => {
    if (selectedRows.length === 0) {
      return false
    }
    return selectedRows.every(date => {
      const intervals = hours[date] || []
      return intervals.every(interval => interval.from === '-' && interval.to === '-')
    })
  }

  const handleSectionClick = (date: string, shiftKey: boolean, ctrlKey: boolean) => {
    if (ctrlKey) {
      setSelectedSections(prevSelectedSections => {
        if (prevSelectedSections.includes(date)) {
          return prevSelectedSections.filter(section => section !== date)
        } else {
          return [...prevSelectedSections, date]
        }
      })
      setLastSelectedSection(date)
    } else if (shiftKey && lastSelectedSection !== null) {
      const startIndex = dayKeys.indexOf(lastSelectedSection)
      const endIndex = dayKeys.indexOf(date)
      const selectedRange = dayKeys.slice(Math.min(startIndex, endIndex), Math.max(startIndex, endIndex) + 1)

      setSelectedSections(selectedRange)
    } else {
      setSelectedSections([date])
      setLastSelectedSection(date)
      handleSectionExpansion(date)
    }
  }

  const handleTimeIntervalAdd = (date: string) => {
    const lastHour = hours[date][hours[date].length - 1]

    if (hours[date].length >= maxIntervalsNumber) return

    if (lastHour.notShow) {
      setHours(prevState => {
        const updatedHours = { ...prevState }
        updatedHours[date] = [
          {
            ...updatedHours[date][0],
            from: '-',
            to: '-',
            notShow: false,
          },
        ]
        return updatedHours
      })
      return
    }

    if (
      !dayjs(lastHour.from, DateFormat.HourMinute).isValid() ||
      !dayjs(lastHour.to, DateFormat.HourMinute).isValid()
    ) {
      displayNotification({ message: t('page.schedule.fillCurrentTimeInterval'), type: 'error' })
      return
    }

    setHours(prevState => {
      const updatedHours = { ...prevState }
      let lastInterval = prevState[date][prevState[date].length - 1]

      const midnight = dayjs('00:00', 'HH:mm')
      if (dayjs(lastInterval.to, DateFormat.HourMinute).isSame(midnight, 'minute') && prevState[date].length > 1) {
        lastInterval = prevState[date][prevState[date].length - 2]
      }

      const newTimeInterval: HoursInterval = {
        id: Math.random().toString(),
        from: lastInterval.to,
        to: '-',
      }
      updatedHours[date] = sortIntervals([...updatedHours[date], newTimeInterval])
      return updatedHours
    })
  }

  const handleTimeIntervalRemove = (date: string, intervalId: string) => {
    setHours(prevState => {
      const updatedHours = { ...prevState }
      if (updatedHours[date].length === 1) {
        return {
          ...prevState,
          [date]: [
            {
              ...updatedHours[date][0],
              from: '-',
              to: '-',
              notShow: true,
            },
          ],
        }
      }

      const filtered = updatedHours[date].filter(interval => interval.id !== intervalId)
      updatedHours[date] = sortIntervals(filtered)
      return updatedHours
    })
  }

  const handleTimeIntervalChange = (intervalId: string, date: string, newTime: string, isFromHour: boolean) => {
    setHours(prevHours => {
      const updatedIntervals = prevHours[date].map(interval => {
        if (interval.id === intervalId) {
          return {
            ...interval,
            [isFromHour ? 'from' : 'to']: newTime,
          }
        }
        return interval
      })
      return {
        ...prevHours,
        [date]: sortIntervals(updatedIntervals),
      }
    })
  }

  const theme = useTheme()

  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'))
  const mobileTableMinWidth = 640
  const mobileTableMinWidthPx = `${mobileTableMinWidth}px`

  const isValidTime = (time: string): boolean => {
    return dayjs(time, 'HH:mm', true).isValid()
  }

  const filterInvalidHours = (intervals: HoursInterval[]): HoursInterval[] => {
    return intervals.filter(interval => {
      const fromTime = dayjs(interval.from, 'HH:mm')
      const toTime = dayjs(interval.to, 'HH:mm')

      return (
        isValidTime(interval.from) &&
        isValidTime(interval.to) &&
        (fromTime.isBefore(toTime) || toTime.isSame(dayjs('00:00', 'HH:mm'), 'minute'))
      )
    })
  }

  const checkIfRecordsAtDayChanged = (date: string): boolean => {
    if (oldHours[date] === undefined) {
      return true
    }
    const old = oldHours[date].map(interval => `${interval.from}-${interval.to}`).sort()
    const newHours = hours[date].map(interval => `${interval.from}-${interval.to}`).sort()
    return old.join('') !== newHours.join('')
  }

  const onClickAway = () => {
    if (!isEditable && openedSection !== '') {
      setOpenedSection('')
    }
  }

  const setNotShow = (date: string) => {
    setHours(prevState => {
      const updatedHours = { ...prevState }
      updatedHours[date] = [
        {
          ...updatedHours[date][0],
          notShow: false,
        },
      ]
      return updatedHours
    })
  }

  const handleSave = (date: string) => {
    if (hours[date].length === 1 && hours[date][0].from === '-' && hours[date][0].to === '-') {
      if (checkIfRecordsAtDayChanged(date)) {
        createRequest(date, hours)
      }
      setIsEditable(false)
      setIsEditMode(false)
      setNotShow(date)
      return
    }

    if (!checkIfRecordsAtDayChanged(date)) {
      setIsEditable(false)
      setIsEditMode(false)
      setNotShow(date)
      return
    }

    const updatedHours = { ...hours, [date]: filterInvalidHours(hours[date]) }
    if (updatedHours[date].length === 0) {
      displayNotification({ message: t('page.schedule.noValidIntervals'), type: 'warning' })
      return
    }

    if (updatedHours[date].length !== hours[date].length) {
      displayNotification({ message: t('page.schedule.invalidIntervals'), type: 'warning' })
      return
    }

    if (checkIfAnyTimePeriodOverlaps(hours[date])) {
      displayNotification({ message: t('page.schedule.overlappingIntervals'), type: 'warning' })
      return
    }

    if (updatedHours[date] !== oldHours[date]) {
      setHours(() => updatedHours)
      createRequest(date, updatedHours)
      setIsEditable(false)
      setIsEditMode(false)
      setNotShow(date)
    }
  }

  const handleCancel = () => {
    setIsEditable(false)
    setIsEditMode(false)
    setHours(() => oldHours)
  }

  const handleClickHistory = () => {
    setIsHistoryVisible(!isHistoryVisible)
    if (isPlannedHours) {
      setHistoryType('PLANNED')
    } else {
      setHistoryType('TRACKED')
    }
  }

  useEffect(() => {
    setIsChangePresent?.(!isEqual(oldHours, hours))
  }, [hours, oldHours, setIsChangePresent])

  return (
    <MasonryTile
      sx={{
        marginBottom: 0,
        gap: 0,
        p: 0,
        px: { xs: 0, sm: 3 },
        minWidth: { xs: mobileTableMinWidthPx, sm: 'auto' },
      }}
    >
      <StyledScheduleStickyTitle
        ref={stickyHeaderRef}
        sx={{
          px: { xs: 1, sm: 3 },
          py: 2,
          gap: 2,
          width: '100%',
          minWidth: { xs: mobileTableMinWidthPx, sm: 'auto' },
        }}
      >
        {!isMediumScreen ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <StyledTitle component={'h2'} variant={'h4'} theme={theme}>
              {isPlannedHours ? t('page.schedule.planned') : t('page.schedule.tracked')}
            </StyledTitle>
            {isPlannedHours && !isReadOnly ? <ScheduleModifyManyDialog date={actualYearMonth} /> : <Box />}
            <Tooltip title={t('components.tooltip.scheduleModificationHistory')}>
              <span>
                <SquareButton
                  color="secondary"
                  onClick={() => handleClickHistory()}
                  aria-label={t('components.tooltip.scheduleModificationHistory')}
                >
                  <HistoryIcon />
                </SquareButton>
              </span>
            </Tooltip>
          </Box>
        ) : (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-start',
            }}
          >
            <StyledTitle sx={{ ml: 0 }} component={'h2'} variant="h4" theme={theme}>
              {isPlannedHours ? t('page.schedule.planned') : t('page.schedule.tracked')}
            </StyledTitle>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%',
                gap: { xs: 1, sm: 0.5 },
              }}
            >
              {isPlannedHours && !isReadOnly && <ScheduleModifyManyDialog date={actualYearMonth} />}
              <Tooltip title={t('components.tooltip.scheduleModificationHistory')}>
                <span>
                  <ToggleHistoryButton
                    disabled={false}
                    setIsHistoryVisible={setIsHistoryVisible}
                    isHistoryVisible={isHistoryVisible}
                  />
                </span>
              </Tooltip>
              {isAnySectionSelected() && !isReadOnly && (
                <Box sx={{ display: 'flex', gap: { xs: 1, sm: 0.5 } }}>
                  <MasonryTileCopyHours
                    isPlannedHours={isPlannedHours}
                    selectedSections={selectedSections}
                    hours={hours}
                    reverseHours={reverseHours}
                    employeeId={employeeId}
                    copyInto={copyInto}
                  />
                  <ScheduleDeletePopup
                    isPlanned={isPlannedHours}
                    selectedRows={selectedSections}
                    onDeleteAction={(_: React.MouseEvent<HTMLElement>, selectedSections: string[]) =>
                      deleteSelected(selectedSections)
                    }
                    isDisabled={false}
                  />
                </Box>
              )}
            </Box>
          </Box>
        )}
        <Drawer
          anchor={'right'}
          open={isHistoryVisible}
          onClose={() => setIsHistoryVisible(false)}
          PaperProps={{ sx: { bgcolor: 'background.main' } }}
        >
          <ModificationHistoryComponent
            selected={selectedUser}
            historyType={historyType}
            selectedDate={dayjs(selectedDate)}
            onLeaveButtonClick={() => {
              setIsHistoryVisible(false)
            }}
          />
        </Drawer>
        <HoursComponent
          hours={totalHours}
          actionDone={isPlannedHours ? t('page.schedule.assigned') : t('page.schedule.logged')}
        />
        {!isMediumScreen && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {!isReadOnly && selectedSections.length > 0 && (
              <MasonryTileCopyHours
                isPlannedHours={isPlannedHours}
                selectedSections={selectedSections}
                hours={hours}
                reverseHours={reverseHours}
                employeeId={employeeId}
                copyInto={copyInto}
              />
            )}
            {!isReadOnly && (
              <ScheduleDeletePopup
                isAllEmpty={checkIfAllSelectedRowsAreEmpty(selectedSections, hours)}
                isPlanned={isPlannedHours}
                selectedRows={selectedSections}
                onDeleteAction={(_: React.MouseEvent<HTMLElement>, selectedSections: string[]) =>
                  deleteSelected(selectedSections)
                }
                isDisabled={!isAnySectionSelected()}
              />
            )}
          </Box>
        )}
      </StyledScheduleStickyTitle>

      <Box sx={{ px: { xs: 1, sm: 3 }, pb: 3, width: '100%' }}>
        <Table
          size="small"
          aria-label="collapsible table"
          stickyHeader
          sx={{
            borderCollapse: 'collapse',
            borderSpacing: '0px 4px',
            width: '100%',
            minWidth: { xs: mobileTableMinWidthPx, sm: 'auto' },
          }}
        >
          <ScheduleTableHeader cells={getHeaderCells(variant)} stickyOffset={stickyOffset} />

          <TableBody>
            <TableRow>
              <StyledTableCell colSpan={getHeaderCells(variant).length} align="center" height={1} />
            </TableRow>
            {dayKeys.map(date => (
              <ScheduleTableSection
                isTogglable={true}
                isReadOnly={isReadOnly}
                key={date}
                date={date}
                intervals={hours[date]}
                isOpen={openedSection === date}
                isSelected={selectedSections.includes(date)}
                isEditable={isEditable}
                onClick={e => {
                  if (isEditable) {
                    return
                  }
                  handleSectionClick(date, e.shiftKey, e.ctrlKey)
                }}
                onClickAway={() => onClickAway()}
                addInterval={() => handleTimeIntervalAdd(date)}
                deleteInterval={intervalId => handleTimeIntervalRemove(date, intervalId)}
                editInterval={(intervalId, date, newTime, isFromHour) =>
                  handleTimeIntervalChange(intervalId, date, newTime, isFromHour)
                }
                setIsEditable={setIsEditable}
                worklogHours={worklogByDate[date]}
                isPlannedHours={isPlannedHours}
                variant={variant}
                save={() => handleSave(date)}
                cancel={handleCancel}
              />
            ))}
          </TableBody>
        </Table>
      </Box>
    </MasonryTile>
  )
}

export default MasonryTileHours
